import React, { useEffect, useState, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import Logo from "../components/Logo";
import { LOGO_SHARED_CHAT_LOADER, LOGO_SIDEBAR_MARK_COLLAPSED, getPublicAssetUrl } from "../config/logoSizes";
import MarkdownMessage from "../components/MarkdownMessage";
import { useApp } from "../context/AppContext";
const fmtDate = (d) => new Date(d).toLocaleDateString(void 0, { year: "numeric", month: "long", day: "numeric" });
const fmtTime = (d) => new Date(d).toLocaleTimeString(void 0, { hour: "2-digit", minute: "2-digit" });
const SharedChat = () => {
  const { shareToken } = useParams();
  const navigate = useNavigate();
  const { user } = useApp();
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [typing, setTyping] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [composerUnlocked, setComposerUnlocked] = useState(false);
  const endRef = useRef(null);
  const inputRef = useRef(null);
  useEffect(() => {
    if (typeof window !== "undefined" && !window.matchMedia("(pointer: coarse)").matches) {
      setComposerUnlocked(true);
    }
  }, []);
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`/api/chat/shared/${shareToken}`);
        setData(res.data);
        setSessionId(res.data.sessionId);
        setMessages(res.data.messages || []);
      } catch (err) {
        setError(err.response?.data?.message || "This shared chat could not be found.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareToken]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, typing]);
  const sendMessage = async () => {
    const txt = input.trim();
    if (!txt || sending) return;
    if (!user) {
      navigate(`/signin?redirect=/shared/${shareToken}`);
      return;
    }
    setInput("");
    setSending(true);
    setTyping(true);
    setMessages((prev) => [...prev, {
      role: "user",
      content: txt,
      timestamp: ( new Date()).toISOString()
    }]);
    try {
      const res = await axios.post("/api/chat/message", {
        message: txt,
        sessionId
      });
      setMessages((prev) => [...prev, {
        role: "assistant",
        content: res.data.message,
        timestamp: ( new Date()).toISOString()
      }]);
      if (res.data.messagesRemaining === 0) setLimitReached(true);
    } catch (err) {
      if (err.response?.status === 403) {
        setLimitReached(true);
      }
      setMessages((prev) => prev.slice(0, -1));
    } finally {
      setSending(false);
      setTyping(false);
    }
  };
  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      // Touch keyboards: Enter inserts a newline (WhatsApp-style) — sending is only via the send button.
      if (window.matchMedia("(pointer: coarse)").matches) return;
      e.preventDefault();
      sendMessage();
    }
  };
  if (loading) return <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "var(--bg-base)", gap: 20 }}>
      <Logo variant="full" size={LOGO_SHARED_CHAT_LOADER} />
      <p style={{ color: "var(--text-3)", fontSize: 14, fontFamily: "Outfit,sans-serif" }}>جاري تحميل المحادثة…</p>
    </div>;
  if (error) return <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", background: "var(--bg-base)", gap: 20, padding: 24 }}>
      <Logo variant="full" size={LOGO_SHARED_CHAT_LOADER} />
      <div style={{ background: "var(--bg-card)", border: "1px solid var(--border2)", borderRadius: 20, padding: "32px 36px", textAlign: "center", maxWidth: 420 }}>
        <div style={{ marginBottom: 14, display: "flex", justifyContent: "center", color: "var(--text-3)" }} aria-hidden="true">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M10 13a5 5 0 0 0 7.07 0l1.41-1.41a5 5 0 0 0 0-7.07 5 5 0 0 0-7.07 0L10 4" />
            <path d="M14 11a5 5 0 0 0-7.07 0L5.52 12.41a5 5 0 0 0 0 7.07 5 5 0 0 0 7.07 0L14 20" />
          </svg>
        </div>
        <h2 style={{ fontFamily: "Outfit,sans-serif", fontSize: 20, fontWeight: 700, color: "var(--text-1)", marginBottom: 10 }}>الرابط غير موجود</h2>
        <p style={{ fontSize: 14, color: "var(--text-2)", lineHeight: 1.7, marginBottom: 24 }}>{error}</p>
        <Link to="/chat" style={{ display: "inline-block", padding: "11px 24px", background: "var(--accent)", color: "#fff", borderRadius: 10, textDecoration: "none", fontSize: 14, fontWeight: 700, fontFamily: "Outfit,sans-serif" }}>
          افتح <span dir="ltr" style={{ unicodeBidi: "isolate" }}>TaweelAI</span> ←
        </Link>
      </div>
    </div>;
  const canSend = !!user && !limitReached;
  return <div className="shared-chat-root">
      {}
      <div style={{ background: "var(--bg-sidebar)", borderBottom: "1px solid var(--border)", padding: "12px 20px", display: "flex", alignItems: "center", gap: 12, flexShrink: 0, zIndex: 10, position: "sticky", top: 0 }}>
        <button
          type="button"
          onClick={() => navigate("/chat")}
          style={{
            border: "none",
            padding: 0,
            margin: 0,
            background: "transparent",
            display: "flex",
            alignItems: "center",
            gap: 10,
            cursor: "pointer",
            textAlign: "start"
          }}
        >
          <Logo variant="mark" size={LOGO_SIDEBAR_MARK_COLLAPSED} style={{ flexShrink: 0, display: "block" }} />
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--text-3)", lineHeight: 1.3 }}>محادثة مشتركة</span>
        </button>
        <div style={{ flex: 1 }} />
        {}
        <div style={{ textAlign: "center", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)", display: "inline-flex", alignItems: "center", gap: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ opacity: 0.85 }}>
              <path d="M12 2l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" />
            </svg>
            {data?.title}
          </span>
          <span style={{ fontSize: 10, color: "var(--text-3)" }}>
            {data?.ownerName} · {fmtDate(data?.sharedAt)} · {data?.viewCount} مشاهدة
          </span>
        </div>
        <div style={{ flex: 1 }} />
        {user ? <button onClick={() => navigate("/chat")} style={{ padding: "7px 14px", background: "var(--accent)", color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>
            محادثاتي ←
          </button> : <Link to={`/signin?redirect=/shared/${shareToken}`} style={{ padding: "7px 14px", background: "var(--accent)", color: "#fff", borderRadius: 8, textDecoration: "none", fontSize: 13, fontWeight: 600 }}>
            سجّل دخولك للمشاركة
          </Link>}
      </div>
      {}
      <div style={{ flex: 1, overflowY: "auto", padding: "20px 16px" }}>
        <div style={{ maxWidth: 760, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
          {}
          <div style={{ background: "var(--bg-card)", border: "1px solid var(--border)", borderRadius: 12, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, marginBottom: 4 }}>
            {data?.ownerAvatar ? <img src={data.ownerAvatar} alt="" style={{ width: 38, height: 38, borderRadius: "50%", border: "2px solid var(--border2)", flexShrink: 0 }} /> : <div style={{ width: 38, height: 38, borderRadius: "50%", background: "var(--accent)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 15, flexShrink: 0 }}>
                  {data?.ownerName?.[0]?.toUpperCase() || "U"}
                </div>}
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-1)" }}>شاركها {data?.ownerName}</div>
              <div style={{ fontSize: 11, color: "var(--text-3)" }}>يمكنك المشاركة في هذه المحادثة</div>
            </div>
          </div>
          {messages.map((msg, i) => <div key={i} style={{ display: "flex", gap: 12, flexDirection: msg.role === "user" ? "row-reverse" : "row" }}>
              <div style={{
    width: 32,
    height: 32,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 2,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: msg.role === "user" ? "#1a5e2e" : "var(--bg-elevated)",
    border: msg.role === "assistant" ? "1px solid var(--border2)" : "none",
    overflow: "hidden"
  }}>
                {msg.role === "assistant" ? <img src={getPublicAssetUrl("small-icon.svg")} alt="" className="msg-assistant-model-icon" draggable={false} /> : <span style={{ color: "#d4f0de", fontSize: 12, fontWeight: 700 }}>{data?.ownerName?.[0]?.toUpperCase() || "U"}</span>}
              </div>
              <div style={{ maxWidth: "76%" }}>
                <div style={{
    padding: "12px 16px",
    borderRadius: 18,
    fontSize: 14,
    lineHeight: 1.75,
    ...msg.role === "user" ? { background: "#1a5e2e", color: "#d4f0de", borderBottomRightRadius: 4, border: "1px solid rgba(61,186,110,.25)" } : { background: "var(--bg-msg-ai)", border: "1px solid var(--border)", color: "var(--text-1)", borderBottomLeftRadius: 4 }
  }}>
                  <MarkdownMessage content={msg.content} isUser={msg.role === "user"} />
                </div>
                <div style={{ fontSize: 10, color: "var(--text-3)", marginTop: 4, textAlign: msg.role === "user" ? "right" : "left" }}>
                  {msg.role === "user" ? data?.ownerName?.split(" ")[0] || "\u0623\u0646\u062A" : <span dir="ltr" style={{ unicodeBidi: "isolate" }}>TaweelAI</span>} · {fmtTime(msg.timestamp)}
                </div>
              </div>
            </div>)}
          {typing && <div style={{ display: "flex", gap: 12 }}>
              <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--bg-elevated)", border: "1px solid var(--border2)", overflow: "hidden", flexShrink: 0 }}>
                <img src={getPublicAssetUrl("small-icon.svg")} alt="" className="msg-assistant-model-icon" draggable={false} />
              </div>
              <div style={{ padding: "14px 18px", background: "var(--bg-msg-ai)", border: "1px solid var(--border)", borderRadius: 18, borderBottomLeftRadius: 4 }}>
                <div className="typing-dots"><span /><span /><span /></div>
              </div>
            </div>}
          <div ref={endRef} />
        </div>
      </div>
      {}
      <div style={{ borderTop: "1px solid var(--border)", background: "var(--bg-sidebar)", padding: "10px 16px 14px", flexShrink: 0 }}>
        <div style={{ maxWidth: 760, margin: "0 auto" }}>
          {}
          {!user && <div style={{ textAlign: "center", marginBottom: 10, padding: "10px", background: "var(--bg-base)", border: "1px solid var(--border2)", borderRadius: 10 }}>
              <span style={{ fontSize: 13, color: "var(--text-2)" }}>
                <Link to={`/signin?redirect=/shared/${shareToken}`} style={{ color: "var(--accent)", fontWeight: 700, textDecoration: "none" }}>سجّل دخولك</Link>
                {" "}أو{" "}
                <Link to={`/signup`} style={{ color: "var(--accent)", fontWeight: 700, textDecoration: "none" }}>أنشئ حساباً</Link>
                {" "}للمشاركة في المحادثة
              </span>
            </div>}
          {}
          {limitReached && <div style={{ textAlign: "center", marginBottom: 10, padding: "10px", background: "rgba(234,179,8,.08)", border: "1px solid rgba(234,179,8,.3)", borderRadius: 10 }}>
              <span style={{ fontSize: 13, color: "#ca8a04" }}>وصلت للحد اليومي — <button onClick={() => navigate("/chat")} style={{ background: "none", border: "none", color: "var(--accent)", cursor: "pointer", fontWeight: 700, fontFamily: "inherit", fontSize: 13 }}>احصل على ساعة كاملة</button></span>
            </div>}
          <div style={{
    display: "flex",
    alignItems: "flex-end",
    gap: 10,
    background: "var(--bg-input)",
    border: "1px solid var(--border2)",
    borderRadius: 16,
    padding: "10px 12px",
    opacity: canSend ? 1 : 0.6
  }}>
            <textarea
    ref={inputRef}
    value={input}
    readOnly={!composerUnlocked}
    onPointerDown={(e) => {
      if (!composerUnlocked) {
        e.currentTarget.readOnly = false;
        setComposerUnlocked(true);
      }
    }}
    onChange={(e) => {
      setInput(e.target.value);
      e.target.style.height = "auto";
      e.target.style.height = Math.min(e.target.scrollHeight, 130) + "px";
    }}
    onKeyDown={handleKey}
    placeholder={user ? "\u0627\u0643\u062A\u0628 \u0631\u0633\u0627\u0644\u062A\u0643 \u0648\u0634\u0627\u0631\u0643 \u0641\u064A \u0627\u0644\u0645\u062D\u0627\u062F\u062B\u0629..." : "\u0633\u062C\u0651\u0644 \u062F\u062E\u0648\u0644\u0643 \u0644\u0644\u0645\u0634\u0627\u0631\u0643\u0629..."}
    disabled={!canSend}
    rows={1}
    dir="rtl"
    style={{ flex: 1, background: "transparent", border: "none", outline: "none", color: "var(--text-1)", fontSize: 14, fontFamily: "inherit", resize: "none", maxHeight: 130, lineHeight: 1.6, padding: "1px 0" }}
  />
            <button
    onClick={sendMessage}
    disabled={!input.trim() || !canSend || sending}
    style={{ width: 36, height: 36, borderRadius: 9, background: "var(--accent)", border: "none", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, opacity: !input.trim() || !canSend || sending ? 0.35 : 1, transition: "opacity .15s" }}
  >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor"><path d="M2 21l21-9L2 3v7l15 2-15 2v7z" /></svg>
            </button>
          </div>
        </div>
      </div>
    </div>;
};
var stdin_default = SharedChat;
export {
  stdin_default as default
};
